import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useFilter } from './FilterContext';

type SearchString = string;
type Attribute = string;

interface VarContains {
  search: Array<SearchString | Record<'var', Attribute>>;
}

interface SearchRule {
  or: Array<VarContains>;
}

const generateSearchFilter = (searchString: string, attributes: string[]): any => {
  const rule: SearchRule = {
    or: [
      {
        search: [{ var: 'name' }, searchString]
      },
      ...attributes.map(attribute => ({
        search: [{ var: `attributes.${attribute.split('.').join('##')}.value` }, searchString]
      }))
    ]
  };

  return {
    _id: 'search',
    name: `Search results for: ${searchString}`,
    rule,
    enabled: true,
    selected: true,
    owner: true
  };
};

const getAttributes = (search: string): Array<string> => {
  const queryString = new URLSearchParams(search);

  const attributes = queryString.get('attributes');

  return attributes
    ? attributes.split(',').map(attribute => attribute.replace(/ *\([^)]*\) */g, ''))
    : [];
};

const SearchTextField = styled(TextField)(({ theme }) => ({
  width: 256,
  '& .MuiInputBase-root': {
    paddingLeft: 0,
    'background-color': 'unset',
    '&:hover, &.Mui-focusVisible': {},
    '&.Mui-active': {},
    '& .MuiInputAdornment-root': {
      marginTop: 'unset'
    }
  },
  '& .MuiInputBase-input': {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    'background-color': 'unset',
    '&:hover, &.Mui-focusVisible': {},
    '&.Mui-active': {}
  }
}));

const Search = () => {
  const { setSearchFilter } = useFilter();
  const { search } = useLocation();
  const attributes = getAttributes(search);
  const [searchTerm, setSearchTerm] = useState('');

  const onChangeHandler = (event: any) => {
    setSearchTerm(event.target.value);
    if (event.target.value.length > 0) {
      setSearchFilter(generateSearchFilter(event.target.value, attributes));
    } else {
      setSearchFilter(undefined);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    setSearchFilter(undefined);
  };

  return (
    <SearchTextField
      size="small"
      onChange={onChangeHandler}
      value={searchTerm}
      id="search-filter-input"
      data-testid="search-filter-input"
      placeholder="Search this view"
      variant="filled"
      InputProps={{
        startAdornment: (
          <InputAdornment position="end">
            <SearchIcon />
          </InputAdornment>
        ),
        endAdornment:
          searchTerm.length > 0 ? (
            <InputAdornment position="end">
              <CloseIcon onClick={clearSearch} style={{ cursor: 'pointer' }} fontSize="small" />
            </InputAdornment>
          ) : null
      }}
    />
  );
};

export default Search;
